// Single source of truth for categorical colour across the app: person
// identity, venue/project colour, KPI tiles, and chart series. Every
// component that needs one of these MUST import from here instead of
// defining its own hex map — that duplication is exactly what caused Zac to
// be green on one screen and orange on another.
//
// Recharts renders SVG fill/stroke as plain string props that never pass
// through the browser's CSS engine, so a `var(--rmc-*)` string resolves to
// nothing there — charts need a literal value. Everything below is exported
// as a literal hex for that reason. CSS files should keep using the
// `--rmc-*` custom properties in App.css (set to these same values) rather
// than importing this file.

// Person identity — matches the live --rmc-p-* tokens in App.css exactly.
// Only 4 of the app's 21 salespeople carry a dedicated identity colour.
const PERSON_COLOR = {
  zac: '#2F7A5B',
  ginny: '#3B5BDB',
  jq: '#7048B6',
  zen: '#B04A3E',
};

// Fallback for every salesperson with no dedicated identity colour.
export const DEFAULT_PERSON_COLOR = '#5A6B7C';

// Resolve a person's identity colour by name (case-insensitive). Always
// returns a colour — never `undefined` — so callers can drop it straight
// into inline styles or Recharts props without a manual fallback.
export function personColor(name) {
  const key = String(name || '').trim().toLowerCase();
  return PERSON_COLOR[key] || DEFAULT_PERSON_COLOR;
}

// Extended categorical ramp for venues, projects, KPI tiles and chart
// series. Same muted register as the person tokens above, ordered so
// adjacent entries stay visually distinct.
export const CATEGORICAL = [
  '#3B5BDB', // blue
  '#2F7A5B', // green
  '#7048B6', // violet
  '#B04A3E', // clay
  '#96702C', // ochre
  '#2A7B8C', // teal
  '#8C4A6B', // plum
  '#5A6B7C', // slate
];

// Stable colour assignment from the ramp.
// - Pass a numeric `index` (e.g. a row or series position) for the common
//   case — wraps around past the end of the ramp via modulo.
// - Pass only `key` (a string) when no index is available and you need a
//   deterministic pick for a dynamic value — e.g. colouring by a key whose
//   position in a list isn't known ahead of time.
export function categoryColor(key, index) {
  const n = CATEGORICAL.length;
  if (typeof index === 'number' && Number.isFinite(index)) {
    return CATEGORICAL[((index % n) + n) % n];
  }
  const str = String(key ?? '');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return CATEGORICAL[hash % n];
}
